/**
 * Bulk Quantity Selector - JavaScript Component
 * Related to: Bulk Quantity Selector Theme Block
 * 
 * Updated: 2025-07-24
 * Changes:
 * - Optimized for performance with event delegation
 * - Added requestIdleCallback for lazy initialization
 * - Improved keyboard navigation with arrow keys
 * - Added custom events for integration
 * - Enhanced accessibility with ARIA attributes
 * - Reduced memory footprint
 * 
 * Dependencies: bulk-quantity-selector.css
 * Used in: sections/main-product.liquid (bulk-quantity-selector block)
 */
class BulkQuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.isInitialized = false;
  }

  connectedCallback() {
    if (!this.isInitialized) {
      this.init();
      this.isInitialized = true;
    }
  }

  init() {
    this.setupElements();
    if (!this.options || !this.quantityInput) return;
    
    this.bindEvents();
    this.setInitialSelection();
  }

  setupElements() {
    this.options = this.querySelectorAll('.bulk-quantity-selector__option');
    this.quantityInput = document.querySelector('input[name="quantity"]');
    
    // Set ARIA attributes
    this.options.forEach((option, index) => {
      option.setAttribute('role', 'radio');
      option.setAttribute('tabindex', index === 0 ? '0' : '-1');
      option.setAttribute('aria-checked', 'false');
    });
  }

  bindEvents() {
    // Use event delegation for better performance
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('keydown', this.handleKeyboard.bind(this));
    
    // Listen for quantity changes
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', this.handleQuantityChange.bind(this));
    }
  }

  handleClick(e) {
    const option = e.target.closest('.bulk-quantity-selector__option');
    if (option && this.contains(option)) {
      e.preventDefault();
      this.selectOption(option);
    }
  }

  handleKeyboard(e) {
    const option = e.target.closest('.bulk-quantity-selector__option');
    if (!option) return;

    switch(e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.selectOption(option);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        this.focusNextOption(option);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        this.focusPreviousOption(option);
        break;
    }
  }

  focusNextOption(currentOption) {
    const options = Array.from(this.options);
    const currentIndex = options.indexOf(currentOption);
    const nextIndex = (currentIndex + 1) % options.length;
    this.focusOption(options[nextIndex]);
  }

  focusPreviousOption(currentOption) {
    const options = Array.from(this.options);
    const currentIndex = options.indexOf(currentOption);
    const previousIndex = (currentIndex - 1 + options.length) % options.length;
    this.focusOption(options[previousIndex]);
  }

  focusOption(option) {
    this.options.forEach(opt => opt.setAttribute('tabindex', '-1'));
    option.setAttribute('tabindex', '0');
    option.focus();
  }

  selectOption(option) {
    const quantity = parseInt(option.dataset.quantity);
    if (!quantity || isNaN(quantity)) return;

    // Update quantity input
    if (this.quantityInput) {
      this.quantityInput.value = quantity;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update visual selection
    this.updateSelection(option);

    // Update price display
    this.updatePriceDisplay();
  }

  updateSelection(selectedOption) {
    // Remove previous selection
    this.options.forEach(option => {
      option.classList.remove('bulk-quantity-selector__option--selected');
      option.setAttribute('aria-checked', 'false');
    });

    // Add new selection
    selectedOption.classList.add('bulk-quantity-selector__option--selected');
    selectedOption.setAttribute('aria-checked', 'true');
  }

  setInitialSelection() {
    const currentQuantity = parseInt(this.quantityInput?.value) || 1;
    
    // Find matching option or default to first
    let selectedOption = this.options[0];
    this.options.forEach(option => {
      if (parseInt(option.dataset.quantity) === currentQuantity) {
        selectedOption = option;
      }
    });

    this.updateSelection(selectedOption);
  }

  handleQuantityChange() {
    this.setInitialSelection();
  }

  updatePriceDisplay() {
    // Emit custom event for other components
    this.dispatchEvent(new CustomEvent('bulk-quantity:change', {
      bubbles: true,
      detail: {
        quantity: this.quantityInput?.value || 1
      }
    }));
  }

  disconnectedCallback() {
    this.cache.clear();
  }
}

// Register custom element
if (!customElements.get('bulk-quantity-selector')) {
  customElements.define('bulk-quantity-selector', BulkQuantitySelector);
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeBulkSelectors);
} else {
  initializeBulkSelectors();
}

function initializeBulkSelectors() {
  // Use requestIdleCallback for non-critical initialization
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      document.querySelectorAll('.bulk-quantity-selector').forEach(selector => {
        if (!selector.isInitialized) {
          selector.connectedCallback?.();
        }
      });
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      document.querySelectorAll('.bulk-quantity-selector').forEach(selector => {
        if (!selector.isInitialized) {
          selector.connectedCallback?.();
        }
      });
    }, 0);
  }
}